/**
 * Types mirroring the backend response shapes (see apps/api/src/officeReads.ts,
 * apps/api/src/routes/office.ts on the server). EPIC 0A: PERMISSIONS/
 * Permission/OFFICE_ROLES/OfficeRole are no longer hand-duplicated here -
 * packages/foundation is a real npm workspace already linked into
 * node_modules/@wms/foundation, so this just re-exports the one real
 * definition instead of keeping a second copy that could drift from
 * packages/foundation/src/authz/permissions.ts (the actual enforced
 * boundary - see that file's own docstring). Imported via the
 * '@wms/foundation/authz' SUBPATH, not the package's root barrel
 * ('@wms/foundation') - the root barrel's index.ts transitively pulls in
 * packages/foundation/src/ids/ids.ts, which imports node:crypto and breaks
 * a Vite/rollup browser build ("randomUUID is not exported by
 * __vite-browser-external"). authz/index.ts's own dependency chain
 * (permissions.ts -> errors/index.ts -> DomainError.ts) has no such
 * dependency, so packages/foundation/package.json now declares it as a
 * second, browser-safe entry point. The rest of this file's response-shape
 * DTOs remain hand-synced; see docs/architecture/FEATURE_MAP.md ("API type
 * generation") for what OpenAPI-generated codegen now covers instead and
 * why these are not part of it yet.
 */
export { PERMISSIONS, OFFICE_ROLES } from '@wms/foundation/authz';
import type { Permission, OfficeRole } from '@wms/foundation/authz';
export type { Permission, OfficeRole };

export type Self = {
  readonly membership_id: string;
  readonly user_id: string;
  readonly organization_id: string;
  readonly role: OfficeRole;
  readonly warehouse_id: string | null;
  readonly permissions: readonly Permission[];
  /** ADR-084: SESSION = signed in with email + password; API_TOKEN = service key. */
  readonly auth_kind?: 'SESSION' | 'API_TOKEN';
  readonly display_name?: string | null;
  readonly email?: string | null;
  readonly organization_name?: string | null;
  readonly warehouse_name?: string | null;
  readonly has_password?: boolean;
};

export type StockSummary = {
  readonly qty_physical: string;
  readonly qty_in_bins: string;
  readonly qty_pickable: string;
  readonly qty_available: string;
  readonly qty_quarantine: string;
  readonly qty_in_process: string;
  readonly qty_shipped: string;
};

export type Page<T> = { readonly rows: readonly T[]; readonly total: number; readonly summary?: StockSummary };

export type Seller = {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly active: boolean;
  readonly contract_id: string | null;
  readonly contract_state: string | null;
  readonly contract_currency: string | null;
  readonly legal_entity_name: string | null;
  readonly tariff_id: string | null;
  readonly tariff_name: string | null;
  readonly stock_sku_count: string;
  readonly open_orders: string;
  readonly open_discrepancies: string;
  readonly last_activity: string;
};

export type SellerDetail = {
  readonly owner: Record<string, unknown>;
  readonly contracts: readonly Record<string, unknown>[];
  readonly stockByWarehouse: readonly Record<string, unknown>[];
  readonly openOrders: readonly Record<string, unknown>[];
  readonly openInbounds: readonly Record<string, unknown>[];
};

export type InventoryRow = {
  readonly sku_id: string;
  readonly sku_code: string;
  readonly sku_name: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly warehouse_id: string;
  readonly warehouse_name: string;
  readonly location_id: string;
  readonly location_code: string;
  readonly location_path: string;
  readonly location_kind: string;
  readonly stock_bucket: 'BIN' | 'QUARANTINE' | 'IN_PROCESS' | 'SHIPPED';
  readonly lot_id: string | null;
  readonly qty_on_hand: string;
  readonly qty_reserved: string;
  readonly qty_available: string;
  readonly last_movement: string;
};

export type InventoryDetail = {
  readonly sku: Record<string, unknown>;
  readonly ownerId: string;
  readonly balances: readonly Record<string, unknown>[];
  readonly reservations: readonly Record<string, unknown>[];
  readonly movements: readonly Record<string, unknown>[];
};

export type InboundRow = {
  readonly awaiting_putaway?: string;
  readonly id: string;
  readonly number: string;
  readonly kind: string;
  readonly state: string;
  readonly expected_at: string | null;
  readonly arrived_at: string | null;
  readonly created_at: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly warehouse_id: string;
  readonly warehouse_name: string;
  readonly line_count: string;
  readonly qty_expected: string;
  readonly qty_received: string;
  readonly discrepancy_count: string;
  /** Request revisions (ADR-089): the client edited the request after it was sent. */
  readonly revision_no?: number;
  readonly last_edited_at?: string | null;
  readonly edited_after_submit?: boolean;
};

/** One revision of a request the client edited after sending it; `diff` is request fields + quantities only. */
export type RequestRevision = {
  readonly revision_no: number;
  readonly created_at: string;
  readonly actor_kind: 'SELLER' | 'OFFICE';
  readonly actor_name: string | null;
  readonly diff: {
    readonly fields: readonly { readonly field: string; readonly from: string | null; readonly to: string | null }[];
    readonly lines: readonly { readonly op: 'ADDED' | 'REMOVED' | 'CHANGED'; readonly skuId: string; readonly skuCode: string; readonly from: number | null; readonly to: number | null }[];
  };
};

export type PutawayAwaitingRow = {
  readonly warehouse_id: string;
  readonly warehouse_name: string;
  readonly location_id: string;
  readonly location_code: string;
  readonly sku_id: string;
  readonly sku_code: string;
  readonly sku_name: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly is_serial_tracked: boolean;
  readonly qty_on_hand: string;
  readonly held_overage: string;
  readonly planned: string;
  readonly unplanned: string;
  readonly waits_for_return: boolean;
  readonly since: string;
};

export type PutawayQueueTask = {
  readonly id: string;
  readonly state: string;
  readonly warehouse_name: string;
  readonly location_code: string | null;
  readonly sku_code: string | null;
  readonly sku_name: string | null;
  readonly owner_name: string | null;
  readonly qty_target: string;
  readonly qty_done: string;
  readonly created_at: string;
  readonly claimed_by: string | null;
  readonly claimed_at: string | null;
  readonly claim_live: boolean | null;
};

export type PutawayQueue = { readonly awaiting: readonly PutawayAwaitingRow[]; readonly tasks: readonly PutawayQueueTask[] };

export type InboundHeader = {
  /** Derived, not a state: what of this supply is still in the receiving zone (shared pool). */
  readonly awaiting_putaway?: string;
  readonly id: string;
  readonly number: string;
  readonly kind: string;
  readonly state: string;
  readonly expected_at: string | null;
  readonly arrived_at: string | null;
  readonly created_at: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly warehouse_id: string;
  readonly warehouse_name: string;
  /** Optional so an older server answer still renders. */
  readonly warehouse_code?: string;
  readonly requested_by_seller: boolean;
  readonly revision_no?: number;
  readonly last_edited_at?: string | null;
  readonly edited_after_submit?: boolean;
};

export type InboundLine = {
  readonly id: string;
  readonly sku_id: string | null;
  readonly sku_code: string | null;
  readonly sku_name: string | null;
  readonly article: string | null;
  readonly size: string | null;
  readonly color: string | null;
  /** Canonical barcode first (server order), then the others. */
  readonly barcodes: readonly string[] | null;
  readonly is_serial_tracked: boolean | null;
  readonly qty_expected: string;
  readonly qty_received: string;
  readonly discrepancy: string;
  readonly responsible_party: string;
  readonly discrepancy_note: string | null;
  /** Decision on a surplus: PENDING (waiting; held) / RESOLVED (accepted) / REJECTED (held, needs separate handling) / DISPOSED (handled). */
  readonly overage_resolution?: 'PENDING' | 'RESOLVED' | 'REJECTED' | 'DISPOSED';
  /** Units of this line's surplus still held (surplus minus confirmed disposals). */
  readonly overage_held?: string;
};

export type ReceivingLocation = { readonly id: string; readonly code: string; readonly kind: 'RECEIVING' | 'QUARANTINE' };

export type InboundReceipt = {
  readonly id: string;
  readonly inbound_line_id: string;
  readonly qty: string;
  readonly occurred_at: string;
  readonly location_code: string;
  readonly actor_name: string | null;
  readonly by_device: boolean;
};

export type InboundActivity = {
  readonly id: string;
  readonly action: string;
  readonly entity_kind: string;
  readonly occurred_at: string;
  readonly payload: Record<string, unknown>;
  readonly actor_name: string | null;
};

export type InboundDetail = {
  readonly inbound: InboundHeader;
  readonly lines: readonly InboundLine[];
  readonly dispositions: readonly Record<string, unknown>[];
  readonly receiving_locations: readonly ReceivingLocation[];
  readonly receipts: readonly InboundReceipt[];
  readonly activity: readonly InboundActivity[];
  readonly revisions?: readonly RequestRevision[];
};

export type InboundReceiveInput = {
  readonly opId: string;
  readonly inboundLineId: string;
  readonly qty: string;
  readonly locationId: string;
  readonly skuId?: string;
  readonly discrepancy?: string;
  readonly responsibleParty?: string;
  readonly note?: string;
};
export type PutawayStockRow = {
  readonly location_id: string;
  readonly location_code: string;
  readonly sku_id: string;
  readonly sku_code: string;
  readonly sku_name: string;
  readonly article: string;
  readonly size: string | null;
  readonly color: string | null;
  readonly barcodes: readonly string[] | null;
  readonly lot_id: string | null;
  readonly is_serial_tracked: boolean;
  readonly qty_on_hand: string;
  readonly qty_available: string;
};
export type PutawayOverview = {
  readonly warehouse: { readonly id: string; readonly code: string; readonly name: string };
  /** The system zones of the inbound's own warehouse; null = this warehouse has none of that kind. */
  readonly receiving: { readonly id: string; readonly code: string } | null;
  readonly quarantine: { readonly id: string; readonly code: string } | null;
  readonly stock: readonly PutawayStockRow[];
  readonly open_tasks: { readonly count: number; readonly qty_remaining: string };
};
export type PutawayTarget = {
  readonly id: string;
  readonly code: string;
  readonly zone_name: string | null;
  readonly sku_count: number;
  readonly qty_total: string;
  readonly sku_codes: readonly string[];
};
export type PutawayInput = {
  readonly opId: string;
  readonly sourceLocationId: string;
  readonly skuId: string;
  readonly lotId?: string;
  readonly qty: string;
  readonly toLocationId: string;
};
export type PutawayResult = { readonly toLocationId: string; readonly toLocationCode: string; readonly qtyPlaced: string; readonly replayed: boolean };
export type InboundReceiveResult = { readonly movementId: string | null; readonly qtyReceivedTotal: string; readonly identified: boolean; readonly replayed: boolean };

export type OrderRow = {
  readonly id: string;
  readonly number: string;
  readonly state: string;
  readonly priority: string | null;
  readonly due_at: string | null;
  readonly channel_id: string | null;
  readonly channel_name: string | null;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly warehouse_id?: string;
  readonly warehouse_name?: string | null;
  readonly created_at: string;
  readonly qty_ordered: string;
  readonly qty_reserved: string;
  readonly qty_picked: string;
  readonly qty_packed: string;
  readonly qty_shipped: string;
  readonly revision_no?: number;
  readonly last_edited_at?: string | null;
  readonly edited_after_submit?: boolean;
  /** The order's current physical shipment (not rejected), when it has one: the unified "Shipments" list columns. */
  readonly shipment_number?: string | null;
  readonly shipment_state?: string | null;
  readonly carrier_kind?: string | null;
  readonly carrier_ref?: string | null;
  readonly departed_at?: string | null;
  readonly requested_in_portal?: boolean | null;
  readonly fulfilment_model?: FulfilmentModel;
};

/** Ops create-order flow - lookups and the create result. */
export type ManualOrderOptions = {
  readonly sellers: readonly { readonly id: string; readonly code: string; readonly name: string }[];
  readonly warehouses: readonly { readonly id: string; readonly code: string; readonly name: string }[];
};

export type ManualOrderSku = {
  readonly sku_id: string;
  readonly code: string;
  readonly name: string;
  readonly barcode: string | null;
  /** Canonical barcode first, then the others (server order). */
  readonly barcodes?: readonly string[];
  readonly article?: string;
  readonly size?: string;
  readonly color?: string;
  /** Stock projection `available` bucket (pickable BIN on-hand minus reserved), decimal string. */
  readonly available: string;
};

export type ManualOrderShipTo = {
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
};

export type CreateManualOrderInput = {
  readonly idempotencyKey: string;
  readonly ownerId: string;
  readonly warehouseId: string;
  readonly number?: string | null;
  readonly dueAt?: string | null;
  readonly shipTo?: ManualOrderShipTo | null;
  /** The operator's choice (the form requires it); an older client may omit it and the order is then UNKNOWN. */
  readonly fulfilmentModel?: 'STANDARD' | 'FBS';
  readonly lines: readonly { readonly skuId: string; readonly qty: string }[];
};

/** Ops create-supply form (RECEIVING.WRITE): lookups and the create result. */
export type CreateInboundInput = {
  readonly idempotencyKey: string;
  readonly ownerId: string;
  readonly warehouseId: string;
  readonly reference?: string | null;
  readonly expectedAt?: string | null;
  readonly lines: readonly { readonly skuId: string; readonly qty: string }[];
};

/** The warehouses that may receive this seller's supply; `reason` says why there are none. */
export type InboundWarehouses = {
  readonly warehouses: readonly { readonly id: string; readonly code: string; readonly name: string }[];
  readonly reason: 'READY' | 'NO_WAREHOUSE' | 'CONTRACT_NOT_ACTIVE' | 'OUT_OF_SCOPE';
};

export type CreateInboundResult = {
  readonly inboundShipmentId: string;
  readonly number: string;
  readonly state: string;
  readonly lineCount: number;
  readonly replayed: boolean;
};

export type CreateManualOrderResult = {
  readonly salesOrderId: string;
  readonly number: string;
  readonly state: string;
  readonly lineCount: number;
  readonly replayed: boolean;
};

export type TariffSummary = {
  readonly id: string;
  readonly name: string;
  readonly currency: string;
  readonly valid_from: string;
  readonly valid_to: string | null;
  readonly active_rule_count: number;
};

export type OrderDetail = {
  readonly order: Record<string, unknown>;
  readonly lines: readonly Record<string, unknown>[];
  readonly activity: readonly Record<string, unknown>[];
  readonly revisions?: readonly RequestRevision[];
  /** Execution picture (read-only): physical shipments, held/consumed stock with its cell, pick tasks. */
  readonly shipments?: readonly OrderShipmentRef[];
  readonly reservations?: readonly OrderReservation[];
  readonly tasks?: readonly OrderPickTask[];
};

/** How an order is fulfilled; UNKNOWN = not classified (historical or entered without a choice). */
export type FulfilmentModel = 'STANDARD' | 'FBS' | 'UNKNOWN';

export type OrderShipmentRef = {
  readonly id: string;
  readonly number: string;
  readonly state: string;
  readonly carrier_kind: string | null;
  readonly carrier_ref: string | null;
  readonly departed_at: string | null;
  readonly package_count: string;
};

export type OrderReservation = {
  readonly id: string;
  readonly sku_code: string;
  readonly location_code: string;
  readonly qty: string;
  readonly state: string;
};

export type OrderPickTask = {
  readonly id: string;
  readonly state: string;
  readonly sku_code: string;
  readonly location_code: string | null;
  readonly qty_target: string | null;
  readonly qty_done: string;
};

export type ShipmentRow = {
  readonly id: string;
  readonly number: string;
  readonly state: string;
  readonly carrier_kind: string | null;
  readonly carrier_ref: string | null;
  readonly departed_at: string | null;
  readonly created_at: string;
  readonly package_count: string;
};

export type ShipmentDetail = {
  readonly shipment: Record<string, unknown>;
  readonly packages: readonly Record<string, unknown>[];
  readonly contents: readonly Record<string, unknown>[];
  readonly exitMovements: readonly Record<string, unknown>[];
};

export type ReturnRow = {
  readonly id: string;
  readonly number: string;
  readonly state: string;
  readonly arrived_at: string | null;
  readonly created_at: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly qty_received: string;
  readonly qty_dispositioned: string;
  readonly discrepancy_count: string;
};

export type ReturnDetail = {
  readonly return: Record<string, unknown>;
  readonly lines: readonly Record<string, unknown>[];
  readonly dispositions: readonly Record<string, unknown>[];
};

/**
 * REGULATED EVIDENCE epic (ADR-074) UI wiring - photo evidence DTOs, matching
 * `packages/operations/src/services/evidence.ts`'s `EvidenceRecord` minus
 * `storageKey` (never sent to a client, apps/api/src/routes/office.ts strips
 * it from every response).
 */
export type EvidenceKind = 'RECEIVING_DISCREPANCY' | 'DAMAGE' | 'UNKNOWN_ITEM' | 'RETURN_INSPECTION' | 'QUARANTINE' | 'WRITE_OFF';
export type EvidenceEntityKind = 'INBOUND_LINE' | 'SALES_ORDER' | 'RETURN_DISPOSITION' | 'PACKAGE';

export type EvidenceRow = {
  readonly id: string;
  readonly kind: EvidenceKind;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly contentHash: string;
  readonly entityKind: string;
  readonly entityId: string;
  readonly ownerId: string;
  readonly skuId: string | null;
  readonly serialUnitId: string | null;
  readonly actorMembershipId: string | null;
  readonly deviceId: string | null;
  readonly retentionDays: number | null;
  readonly deleteAfter: string | null;
  readonly deletedAt: string | null;
  readonly createdAt: string;
};

export type EvidenceWithBytes = EvidenceRow & { readonly bytesBase64: string };

export type ProblemRow = {
  readonly type: 'RECEIVING_DISCREPANCY' | 'RETURN_AWAITING_DISPOSITION' | 'IMPORT_CONFLICT' | 'BILLING_UNPRICED' | 'SHIPMENT_INCOMPLETE';
  readonly entity_id: string;
  readonly entity_label: string;
  readonly seller_name: string | null;
  readonly severity_label: string;
  readonly age_seconds: string;
  readonly created_at: string;
};

export type SlaBlocker = { readonly type: string; readonly detail: Readonly<Record<string, unknown>> };
export type SlaDeadline = {
  readonly effectiveAt: string | null;
  readonly source: 'API' | 'DERIVED' | 'ABSENT';
  readonly startByAt: string | null;
};
export type SlaState = 'normal' | 'approaching_cutoff' | 'urgent' | 'blocked' | 'overdue';
export type QueueBucket = 'TO_PICK' | 'TO_PACK' | 'URGENT' | 'PROBLEMS';

export type QueueRow = {
  readonly salesOrderId: string;
  readonly number: string;
  readonly state: string;
  readonly channelKind: string | null;
  readonly channelId: string | null;
  readonly ownerId: string;
  readonly warehouseId: string;
  readonly createdAt: string;
  readonly dueAt: string | null;
  readonly sla: { readonly deadline: SlaDeadline; readonly state: SlaState; readonly blockers: readonly SlaBlocker[] };
  readonly buckets: readonly QueueBucket[];
};

export type LabelStatus = {
  readonly packageId: string;
  readonly shippingLabelId: string | null;
  readonly format: string | null;
  readonly labelStatus: 'NONE' | 'READY' | 'PRINTED' | 'VOIDED';
  readonly printRequestStatus: 'REQUESTED' | 'PRINTING' | 'PRINTED' | 'FAILED' | null;
  readonly printAttemptCount: number | null;
  readonly printErrorCode: string | null;
  /** False for an order that did not come through a marketplace connection: no transport label can be requested for it. Absent on an older API (treated as supported). */
  readonly transportLabelSupported?: boolean;
};

export type Membership = {
  readonly id: string;
  readonly display_name?: string;
  readonly email?: string;
  readonly role: OfficeRole;
  readonly state: string;
  readonly warehouse_id: string | null;
  readonly warehouse_name?: string | null;
  readonly created_at?: string;
  readonly updated_at?: string;
  readonly [k: string]: unknown;
};

export type ImportBatch = {
  readonly id: string;
  readonly source: string;
  readonly entity_kind: string;
  readonly file_name: string;
  readonly status: string;
  readonly created_at: string;
  readonly [k: string]: unknown;
};

export type Contract = Record<string, unknown>;
export type TariffRule = Record<string, unknown>;
export type Charge = Record<string, unknown>;

/** Marketplace Shared Foundation epic - one connected channel, with the reconciliation worker's sync status folded in. */
export type ChannelRow = {
  readonly id: string;
  readonly ownerId: string;
  readonly kind: string;
  readonly health: string;
  readonly enabled: boolean;
  readonly lastPolledAt: string | null;
  readonly lastSuccessAt: string | null;
  readonly hasCredential: boolean;
  readonly syncStatus: string | null;
  readonly credentialConfigured: boolean;
  readonly credentialRevoked: boolean;
  readonly lastErrorAt: string | null;
  readonly lastErrorMessage: string | null;
  readonly nextPollAt: string | null;
};

export type CredentialStatus = {
  readonly credentialId: string;
  readonly credentialType: string;
  readonly configured: boolean;
  readonly maskedHint: string | null;
  readonly revoked: boolean;
  readonly rotatedAt: string | null;
  readonly createdAt: string;
};

export type SyncStatus = {
  readonly channelId: string;
  readonly status: string;
  readonly cursor: Record<string, unknown>;
  readonly consecutiveFailures: number;
  readonly lastPolledAt: string | null;
  readonly lastSuccessAt: string | null;
  readonly lastErrorAt: string | null;
  readonly lastErrorMessage: string | null;
  readonly nextPollAt: string | null;
} | null;

/* -------------------------------------------------------------------------- */
/*  ADR-076 Part A/D - tenant onboarding readiness                            */
/* -------------------------------------------------------------------------- */

export type OnboardingStep = {
  readonly step: string;
  readonly done: boolean;
  readonly blockerCode: string | null;
  readonly required: boolean;
  readonly detail: Record<string, unknown>;
};

export type OnboardingReadiness = {
  readonly organizationId: string;
  readonly status: 'NOT_STARTED' | 'IN_PROGRESS' | 'BLOCKED' | 'READY_FOR_REVIEW' | 'ACTIVE';
  readonly steps: readonly OnboardingStep[];
  readonly blockers: readonly { code: string; step: string }[];
  readonly reconciliationSkipped: { at: string; by: string; reason: string } | null;
  readonly activatedAt: string | null;
  readonly activatedBy: string | null;
};

/* -------------------------------------------------------------------------- */
/*  ADR-076 Part C - seller portal user management                           */
/* -------------------------------------------------------------------------- */

export type SellerPortalUser = {
  readonly id: string;
  readonly email: string;
  readonly display_name: string;
  readonly state: 'PENDING' | 'ACTIVE' | 'DISABLED';
  readonly created_at: string;
  readonly owner_ids: readonly string[];
  readonly last_login_at: string | null;
  readonly active_sessions: string;
};

export type SellerPortalUserAuditEvent = {
  readonly id: string;
  readonly action: string;
  readonly actor_kind: string;
  readonly actor_membership_id: string | null;
  readonly payload: Record<string, unknown>;
  readonly occurred_at: string;
};

/* -------------------------------------------------------------------------- */
/*  ADR-077 - SaaS subscription, entitlements and quotas                       */
/* -------------------------------------------------------------------------- */

export type SubscriptionUsage = {
  readonly metric: string;
  readonly used: number;
  /** null = unlimited. */
  readonly limit: number | null;
  readonly source: 'PLAN' | 'OVERRIDE' | 'UNMANAGED';
  /** false for RETENTION_DAYS, which is a policy ceiling and blocks nothing. */
  readonly enforced: boolean;
  readonly remaining: number | null;
  /** Server-computed. The UI never derives this - see routes/subscriptionView.ts. */
  readonly exhausted: boolean;
};

export type SubscriptionPlanOption = {
  readonly planId: string;
  readonly code: string;
  readonly displayName: string;
  readonly billingInterval: 'MONTH' | 'YEAR';
  readonly currency: string;
  readonly currentVersion: {
    readonly planVersionId: string;
    readonly version: number;
    readonly basePriceMinor: string;
    readonly currency: string;
    readonly trialDays: number;
    readonly capabilities: readonly string[];
    readonly limits: Readonly<Record<string, number | null>>;
  } | null;
};

export type SubscriptionHistoryEntry = {
  readonly id: string;
  readonly changeKind: string;
  readonly fromStatus: string | null;
  readonly toStatus: string | null;
  readonly actorKind: string;
  readonly reason: string | null;
  readonly createdAt: string;
};

export type SubscriptionView = {
  readonly organizationId: string;
  readonly subscriptionId: string | null;
  readonly subscriptionPlanVersionId: string | null;
  readonly storedStatus: string | null;
  /** UNMANAGED = no plan was ever assigned. See ADR-077 for why that is not deny-by-default. */
  readonly effectiveStatus: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED' | 'EXPIRED' | 'UNMANAGED';
  readonly readOnly: boolean;
  readonly suspendReason: string | null;
  readonly plan: { readonly code: string; readonly displayName: string; readonly billingInterval: string } | null;
  readonly planVersion: number | null;
  readonly basePriceMinor: string | null;
  readonly currency: string | null;
  readonly trialEndsAt: string | null;
  readonly graceEndsAt: string | null;
  readonly currentPeriodStart: string | null;
  readonly currentPeriodEnd: string | null;
  readonly cancelAtPeriodEnd: boolean;
  readonly capabilities: readonly string[];
  /** Bought but withheld by the current status - NOT the same as "not in your plan". */
  readonly blockedCapabilities: readonly string[];
  readonly graceDays: number | null;
  readonly usage: readonly SubscriptionUsage[];
  readonly scheduledChange: {
    readonly planVersionId: string;
    readonly planCode: string;
    readonly planDisplayName: string;
    readonly kind: 'UPGRADE' | 'DOWNGRADE';
    readonly effectiveAt: string;
    readonly requiresApproval: boolean;
  } | null;
  readonly availablePlans: readonly SubscriptionPlanOption[];
  readonly history: readonly SubscriptionHistoryEntry[];
};

/* ADR-083 - warehouses, zones and cells */
export const ZONE_PURPOSES = ['STORAGE', 'FBS', 'DEFECT', 'QUARANTINE', 'OTHER'] as const;
export type ZonePurpose = (typeof ZONE_PURPOSES)[number];
export type SystemLocationKind = 'RECEIVING' | 'STAGING' | 'PACKING' | 'QUARANTINE' | 'VIRTUAL';

export type WarehouseSetupRow = {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly time_zone: string;
  readonly legal_entity_name: string;
  readonly zone_count: number;
  readonly cell_count: number;
  readonly missing_system_kinds: readonly SystemLocationKind[];
  /** Every system zone of this warehouse with its code (null = missing). Optional so an older answer still renders. */
  readonly system_places?: readonly { readonly kind: SystemLocationKind; readonly code: string | null }[];
};

export type WarehouseZone = {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly purpose: ZonePurpose;
  readonly cell_kind: 'BIN' | 'QUARANTINE';
  readonly cell_count: number;
  readonly cells_with_stock: number;
};

export type WarehouseStructure = {
  readonly warehouse: { id: string; code: string; name: string; time_zone: string; legal_entity_name: string };
  readonly systemLocations: readonly { kind: SystemLocationKind; code: string | null; id?: string | null; barcode?: string | null }[];
  readonly zones: readonly WarehouseZone[];
  readonly cells: readonly StructureCell[];
  readonly otherLocations: readonly (Omit<StructureCell, 'zone_id' | 'zone_name'> & { zone_id?: string | null })[];
  /** Archived cells: out of every operation, history intact; may be restored. */
  readonly archivedLocations?: readonly { id: string; zone_id: string | null; code: string; barcode?: string; kind: string; archived_at: string }[];
  readonly cellsTruncated: boolean;
};

/** The server's verdict for one cell - the UI renders it and never decides what may be deleted/archived itself. */
export type LocationActions = {
  readonly is_system?: boolean;
  readonly can_delete?: boolean;
  readonly delete_blocker?: 'SYSTEM' | 'STOCK' | 'HISTORY' | null;
  readonly can_archive?: boolean;
  readonly archive_blocker?: 'SYSTEM' | 'STOCK' | 'OPEN_WORK' | null;
};
export type StructureCell = LocationActions & {
  readonly id: string;
  readonly zone_id: string;
  readonly zone_name?: string;
  readonly code: string;
  /** Stable machine barcode (never changes when the cell is renamed). */
  readonly barcode?: string;
  readonly kind: string;
  readonly is_pickable: boolean;
  readonly pick_sequence?: number;
  readonly has_stock: boolean;
};
export type LabelLayout = 'A4_8UP' | 'LABEL_100x60';
export type LabelScope = 'WAREHOUSE' | 'ZONE' | 'SELECTED';
export type LocationLabelsInput =
  | { scope: 'WAREHOUSE'; layout: LabelLayout }
  | { scope: 'ZONE'; zoneId: string; layout: LabelLayout }
  | { scope: 'SELECTED'; locationIds: string[]; layout: LabelLayout };
export type LocationLabelsResult = {
  readonly filename: string;
  readonly layout: LabelLayout;
  readonly labelCount: number;
  readonly pageCount: number;
  readonly archivedCount: number;
  readonly sizeBytes: number;
  readonly bytesBase64: string;
};
export type UpdateLocationInput = { code?: string; isPickable?: boolean; pickSequence?: number };
export type UpdateLocationResult = { id: string; code: string; path: string; isPickable: boolean; pickSequence: number; labelsOutdated: boolean };

export type CodeSuggestion = {
  readonly suggested: string;
  readonly requested: { code: string; valid: boolean; available: boolean } | null;
};

export type CreateWarehouseInput = {
  legalEntityId: string;
  name: string;
  timeZone: string;
  code?: string;
  codeAuto?: boolean;
  idempotencyKey?: string;
  withSystemLocations?: boolean;
};
export type CreateWarehouseResult = { warehouseId: string; code: string; systemLocationsCreated: readonly string[]; replayed?: boolean };
export type CreateCellsInput = { row: string; rackFrom: number; rackTo: number; levelFrom: number; levelTo: number };
export type CreateCellsResult = { created: number; alreadyExisted: number; firstCode: string; lastCode: string; cellKind: 'BIN' | 'QUARANTINE' };

/* ADR-084 - staff sign-in, invitations, recovery */
export type LoginResult =
  | { readonly sessionToken: string; readonly expiresAt: string }
  | { readonly chooseOrganization: readonly { organizationId: string; name: string }[] };
export type LinkState = 'VALID' | 'EXPIRED' | 'USED' | 'REVOKED' | 'INVALID';
export type InvitationInfo = {
  readonly state: LinkState;
  readonly organizationName?: string;
  readonly email?: string;
  readonly displayName?: string;
  readonly role?: OfficeRole;
  readonly warehouseName?: string | null;
  readonly expiresAt?: string;
};
export type RecoveryInfo = { readonly state: LinkState; readonly organizationName?: string; readonly email?: string; readonly expiresAt?: string };
export type StaffInvitation = {
  readonly id: string;
  readonly email: string;
  readonly display_name: string;
  readonly role: OfficeRole;
  readonly warehouse_id: string | null;
  readonly warehouse_name: string | null;
  readonly status: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
  readonly expires_at: string;
  readonly created_at: string;
  readonly accepted_at: string | null;
};
export type OneTimeLink = { readonly token: string; readonly expiresAt: string; readonly invitationId?: string };

/* Products (SKU) import from an Excel template */
export type SkuImportOwner = { readonly id: string; readonly code: string; readonly name: string };
export type SkuImportStatus = 'NEW' | 'UPDATE' | 'SKIP' | 'ERROR';
export type SkuImportOnExisting = 'SKIP' | 'UPDATE';
export type SkuImportRow = {
  readonly rowNumber: number;
  readonly code: string;
  readonly article?: string;
  readonly size?: string;
  readonly color?: string;
  readonly name: string;
  readonly barcode: string;
  readonly status: SkuImportStatus;
  readonly error?: { readonly code: string; readonly params?: Readonly<Record<string, string>> };
};
export type SkuImportPreview = {
  readonly batchId: string;
  readonly owner: SkuImportOwner;
  readonly totals: { readonly rows: number; readonly new: number; readonly update: number; readonly skip: number; readonly error: number };
  readonly canCommit: boolean;
  readonly alreadyImported: boolean;
  readonly rows: readonly SkuImportRow[];
};
export type SkuImportCommitResult = { readonly created: number; readonly updated: number; readonly skipped: number; readonly replayed: boolean };

/* ---- Pilot exceptions: stock corrections, count flags, order shortfalls, order cancel ---- */
export const STOCK_REASON_CODES = ['RECOUNT', 'FOUND', 'LOST', 'DAMAGED', 'MISPLACED', 'EXPIRED', 'DATA_ERROR', 'OTHER'] as const;
export type StockReasonCode = (typeof STOCK_REASON_CODES)[number];
export const CANCEL_REASON_CODES = ['CLIENT_REQUEST', 'OUT_OF_STOCK', 'DUPLICATE', 'ENTERED_BY_MISTAKE', 'OTHER'] as const;
export type CancelReasonCode = (typeof CANCEL_REASON_CODES)[number];

export type StockPosition = {
  readonly warehouse_id: string;
  readonly location_id: string;
  readonly location_code: string;
  readonly location_kind: string;
  readonly sku_id: string;
  readonly sku_code: string;
  readonly sku_name: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly lot_id: string | null;
  readonly qty_on_hand: string;
  readonly qty_reserved: string;
  readonly needs_count: boolean;
};

export type StockSkuHit = {
  readonly sku_id: string;
  readonly code: string;
  readonly name: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly is_lot_tracked: boolean;
  readonly is_serial_tracked: boolean;
  readonly article?: string;
  readonly size?: string;
  readonly color?: string;
  /** Canonical barcode first, then the others (server order). */
  readonly barcodes?: readonly string[];
};

export type CountFlag = {
  readonly id: string;
  readonly state: string;
  readonly reason: 'SHORT_PICK' | 'MANUAL';
  readonly opened_at: string;
  readonly location_id: string;
  readonly location_code: string;
  readonly sku_id: string;
  readonly sku_code: string;
  readonly sku_name: string;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly order_number: string | null;
};

export type StockCorrection = {
  readonly id: string;
  readonly kind: 'COUNT' | 'MOVE' | 'QUARANTINE' | 'WRITE_OFF';
  readonly created_at: string;
  readonly warehouse_id: string;
  readonly location_id: string;
  readonly location_code: string;
  readonly to_location_id: string | null;
  readonly to_location_code: string | null;
  readonly owner_id: string;
  readonly owner_name: string;
  readonly sku_id: string;
  readonly sku_code: string;
  readonly sku_name: string;
  readonly old_qty: string | null;
  readonly new_qty: string | null;
  readonly delta: string | null;
  readonly reason_code: StockReasonCode;
  readonly note: string | null;
  readonly broken_reservations: number;
  readonly actor_membership_id: string | null;
  readonly actor_name: string | null;
  readonly device_id: string | null;
};

export type OrderShortfall = {
  readonly order_id: string;
  readonly number: string;
  readonly order_state: 'RESERVED' | 'PICKING';
  readonly owner_id: string;
  readonly owner_name: string;
  readonly warehouse_id: string;
  readonly manual: boolean;
  readonly line_id: string;
  readonly sku_id: string;
  readonly sku_code: string;
  readonly sku_name: string;
  readonly article?: string;
  readonly size?: string;
  readonly color?: string;
  readonly barcodes?: readonly string[];
  readonly qty_ordered: string;
  readonly qty_picked: string;
  readonly qty_held: string;
  readonly shortfall: string;
};

export type CountStockInput = {
  opId: string; locationId: string; ownerId: string; skuId: string; lotId?: string;
  countedQty: string; expectedQty?: string; reasonCode: StockReasonCode; note?: string;
};
export type CountStockResult = {
  correctionId: string; oldQty: string; countedQty: string; delta: string; movementId: string | null;
  brokenReservations: number; affectedOrderIds: readonly string[]; flagsResolved: number; replayed: boolean;
};
export type MoveStockKind = 'MOVE' | 'QUARANTINE' | 'WRITE_OFF';
export type MoveStockInput = {
  opId: string; kind: MoveStockKind; fromLocationId: string; toLocationId?: string; ownerId: string; skuId: string; lotId?: string;
  qty: string; reasonCode: StockReasonCode; note?: string;
};
export type MoveStockResult = {
  correctionId: string; kind: MoveStockKind; qty: string; fromLocationId: string; toLocationId: string | null;
  movementIds: readonly string[]; brokenReservations: number; affectedOrderIds: readonly string[]; replayed: boolean;
};
export type ShortfallAction = 'REALLOCATE' | 'REDUCE' | 'CANCEL_LINE';
export type ResolveShortfallInput = { opId: string; lineId: string; action: ShortfallAction; reasonCode: StockReasonCode; note?: string };
export type ResolveShortfallResult = {
  orderId: string; lineId: string; action: ShortfallAction; orderState: string; shortfall: string;
  newTasks: readonly { taskId: string; locationId: string; qty: string }[]; lineCancelled: boolean; replayed: boolean;
};
export type CancelOrderInput = { opId: string; orderId: string; reasonCode: CancelReasonCode; note?: string; goodsReturned?: boolean };
export type CancelOrderResult = {
  orderId: string; number: string; state: 'CANCELLED' | 'CANCELLED_AFTER_PICK'; reservationsReleased: number;
  tasksCancelled: number; requiresReturnToStock: boolean; replayed: boolean;
};

/* Client -> warehouse -> contract onboarding (Ops client card). Mirrors packages/operations/src/services/{contracts,ownerReadiness}.ts. */
export type ContractStandingValue = 'ACTIVE' | 'NOT_ACTIVE';
export type ContractStandingReason = 'NONE' | 'DRAFT' | 'EXPIRED' | 'SUSPENDED' | 'TERMINATED' | 'NOT_STARTED';
export type ContractView = {
  readonly id: string;
  readonly ownerId: string;
  readonly legalEntityId: string;
  readonly legalEntityName: string;
  readonly warehouses: readonly { id: string; code: string; name: string }[];
  readonly state: 'DRAFT' | 'ACTIVE' | 'SUSPENDED' | 'ON_HOLD' | 'TERMINATED';
  readonly standing: ContractStandingValue;
  readonly standingReason: ContractStandingReason | null;
  readonly contractNumber: string | null;
  readonly contractDate: string | null;
  readonly validFrom: string;
  readonly validTo: string | null;
  readonly currency: string;
  readonly tariffId: string | null;
  readonly tariffName: string | null;
  readonly hasDocument: boolean;
  readonly documentFilename: string | null;
  readonly documentSizeBytes: number | null;
  readonly documentUploadedAt: string | null;
};
export type SaveContractInput = {
  ownerId?: string;
  warehouseId?: string;
  contractNumber?: string | null;
  contractDate?: string | null;
  validFrom?: string;
  validTo?: string | null;
  state?: ContractView['state'];
  tariffId?: string | null;
};
export type ContractFormOptions = {
  readonly warehouses: readonly { id: string; code: string; name: string; legalEntityId: string; legalEntityName: string; currency: string }[];
  readonly tariffs: readonly { id: string; name: string; currency: string }[];
};
export type ReadinessStepKey = 'CLIENT' | 'WAREHOUSE' | 'CONTRACT' | 'PORTAL' | 'READY';
export type ReadinessStep = { readonly key: ReadinessStepKey; readonly state: 'done' | 'missing'; readonly detail: Record<string, unknown> };
export type OwnerReadiness = {
  readonly ownerId: string;
  readonly steps: readonly ReadinessStep[];
  readonly ready: boolean;
  readonly missing: readonly ReadinessStepKey[];
  readonly warehouses: readonly { id: string; code: string; name: string; serving: boolean }[];
  readonly contractStanding: ContractStandingValue;
  readonly contractReason: ContractStandingReason | null;
  readonly portalUsers: { readonly active: number; readonly pending: number };
  readonly channelsRequireContract: boolean;
  readonly warehouseEntitlement: { readonly limit: number | null; readonly used: number; readonly remaining: number | null };
};

// --- Scan & Receiving v2 (ADR-090) ------------------------------------------------------------------------
export type ScanLocation = {
  readonly locationId: string;
  readonly code: string;
  readonly kind: string;
  readonly zone: { readonly name: string; readonly code: string } | null;
  readonly archived: boolean;
  readonly isSystem: boolean;
  /** An active, pickable storage cell: the only kind that can receive goods straight from a scan. */
  readonly isStorage: boolean;
  readonly matched: string;
};
export type ScanProductCandidate = {
  readonly skuId: string;
  readonly code: string;
  readonly name: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly article: string;
  readonly size: string;
  readonly color: string;
  readonly packLevel: string | null;
  readonly packQtyInBase: string | null;
  readonly isSerialTracked: boolean;
  readonly isLotTracked: boolean;
};
export type ScanMatch =
  | { readonly kind: 'LOCATION'; readonly location: ScanLocation }
  | { readonly kind: 'PRODUCT'; readonly candidates: readonly ScanProductCandidate[] };
export type ScanResolution =
  | { readonly kind: 'LOCATION' | 'ARCHIVED_LOCATION'; readonly location: ScanLocation }
  | { readonly kind: 'PRODUCT'; readonly candidates: readonly ScanProductCandidate[] }
  | { readonly kind: 'AMBIGUOUS'; readonly matches: readonly ScanMatch[] }
  | { readonly kind: 'UNKNOWN' };
export type ScanResolveInput = {
  readonly warehouseId: string;
  readonly code: string;
  readonly expect?: 'LOCATION' | 'PRODUCT';
  readonly ownerId?: string;
};
export type ScanReceiveMode = 'TO_LOCATION' | 'TO_RECEIVING';
export type ScanReceiveInput = {
  readonly opId: string;
  readonly mode: ScanReceiveMode;
  readonly locationId?: string;
  readonly productBarcode?: string;
  readonly skuId?: string;
  readonly inboundLineId?: string;
  readonly qty?: string;
  readonly discrepancy?: string;
  readonly responsibleParty?: string;
  readonly note?: string;
};
export type ScanLocationTotal = { readonly locationId: string; readonly code: string; readonly kind: string; readonly qtyOnHand: string };
export type ScanReceiveResult = {
  readonly mode: ScanReceiveMode;
  readonly shipmentId: string;
  readonly line: {
    readonly inboundLineId: string;
    readonly skuId: string;
    readonly skuCode: string;
    readonly skuName: string;
    readonly qtyExpected: string;
    readonly qtyReceived: string;
    readonly remaining: string;
    readonly discrepancy: string;
    readonly responsibleParty: string;
  };
  readonly overage: boolean;
  readonly receiptMovementId: string | null;
  readonly putawayMovementIds: readonly string[];
  readonly destination: ScanLocationTotal;
  readonly receiving: ScanLocationTotal | null;
  readonly repeatOfRecent: boolean;
  readonly replayed: boolean;
};
export type ScanMoveInput = {
  readonly opId: string;
  readonly warehouseId: string;
  readonly sourceLocationId: string;
  readonly destinationLocationId: string;
  readonly productBarcode?: string;
  readonly skuId?: string;
  readonly ownerId?: string;
  readonly qty?: string;
  readonly lotId?: string;
  readonly quarantine?: boolean;
};
export type ScanMoveResult = {
  readonly warehouseId: string;
  readonly skuId: string;
  readonly skuCode: string;
  readonly skuName: string;
  readonly ownerId: string;
  readonly qty: string;
  readonly reason: 'MOVE' | 'PUTAWAY';
  readonly movementIds: readonly string[];
  readonly source: ScanLocationTotal;
  readonly destination: ScanLocationTotal;
  readonly repeatOfRecent: boolean;
  readonly replayed: boolean;
};
